import { errorView } from '@krp/client-runtime'

import type {
  ApplyAction,
  ChangeType,
  CheckState,
  CheckTrigger,
  DataKind,
  Divergence,
  IgnoreLevel,
  JobItemAction,
  JobItemState,
  JobState,
  MatchKind,
  PriceTransfer,
  ShownError,
  Subject,
} from './types'

/** Слова раздела: пометка строки всегда словом и значком, цвет только усиливает (CLAUDE.md §7). */
export const CHANGE_LABELS: Readonly<Record<ChangeType, string>> = {
  added: 'Добавлено',
  changed: 'Изменено',
  removed: 'Удалено',
  'only-common': 'Только в каталоге',
}

export const SUBJECT_LABELS: Readonly<Record<Subject, string>> = {
  item: 'Позиция',
  field: 'Поле',
  barcode: 'Штрихкод',
  price: 'Цена',
  photo: 'Фото',
}

export const DATA_KIND_LABELS: Readonly<Record<DataKind, string>> = {
  item: 'Номенклатура',
  barcode: 'Штрихкоды',
  price: 'Цены',
  photo: 'Фото',
}

export const CHECK_TRIGGER_LABELS: Readonly<Record<CheckTrigger, string>> = {
  event: 'по изменению в источнике',
  manual: 'вручную',
  nightly: 'ночная сверка',
}

export const CHECK_STATE_LABELS: Readonly<Record<CheckState, string>> = {
  running: 'идёт',
  completed: 'закончена',
  failed: 'прервалась на ошибке',
}

export const JOB_STATE_LABELS: Readonly<Record<JobState, string>> = {
  queued: 'Отправлено, ждёт очереди',
  running: 'Идёт',
  completed: 'Закончено',
  failed: 'Остановилось на ошибке',
}

export const JOB_ITEM_LABELS: Readonly<Record<JobItemState, string>> = {
  pending: 'ждёт',
  running: 'идёт',
  succeeded: 'применено',
  failed: 'ошибка',
}

export const APPLY_ACTION_LABELS: Readonly<Record<ApplyAction, string>> = {
  copy: 'Передать значение в выбранные источники',
  delete: 'Удалить в выбранных источниках',
  restore: 'Вернуть удалённое в этом источнике',
}

/** Что сделала строка задачи в целевом источнике — для журнала. */
export const JOB_ITEM_ACTION_LABELS: Readonly<Record<JobItemAction, string>> = {
  create: 'Создание',
  update: 'Правка поля',
  archive: 'Удаление',
  restore: 'Возврат удалённого',
  'barcode-add': 'Добавлен штрихкод',
  'barcode-remove': 'Убран штрихкод',
  'photo-add': 'Добавлено фото',
  'photo-remove': 'Убрано фото',
  'price-set': 'Записана цена',
  none: 'Не применялось',
}

export const PRICE_TRANSFER_LABELS: Readonly<Record<PriceTransfer, string>> = {
  same: 'Та же цена',
  'add-vat': 'С надбавкой НДС (цель с НДС)',
  'remove-vat': 'Без НДС (вычесть НДС, образец с НДС)',
}

export const IGNORE_LEVEL_LABELS: Readonly<Record<IgnoreLevel, string>> = {
  record: 'Только эта запись',
  product: 'Весь товар по этому виду данных',
  source: 'Весь источник по виду данных',
}

export const MATCH_LABELS: Readonly<Record<MatchKind, string>> = {
  name: 'по точному имени',
  barcode: 'по штрихкоду',
  both: 'по имени и штрихкоду',
}

/** Чего касается строка: у номенклатуры — позиция целиком или одно поле ядра, у наборов — штрихкод, цена, фото. */
export function subjectOf(divergence: Divergence): Subject {
  if (divergence.dataKind === 'item') return divergence.field === null ? 'item' : 'field'
  return divergence.dataKind
}

/**
 * Что уточняет строка рядом с наименованием: поле ядра, штрихкод, у цены — виды цены источников словами («Розничная ↔ Цена кабинета»; без имени —
 * ключ источника), а не служебное значение набора; у фото — ничего.
 */
export function detailOf(divergence: Divergence): string | null {
  // У фото отпечаток показан у каждой стороны рядом со снимком.
  if (divergence.dataKind === 'photo') return null
  if (divergence.dataKind !== 'price') return divergence.field ?? divergence.subject
  const names = divergence.values.map((value) => value.priceTypeName ?? value.priceTypeKey).filter((name) => name !== null)
  return names.length === 0 ? null : names.join(' ↔ ')
}

/** Ставка НДС у цены источника: «НДС 20 %»; без ставки — `null`. */
export function vatText(percent: number | null): string | null {
  return percent === null ? null : `НДС ${percent.toLocaleString('ru-RU')} %`
}

/** «12.10.2026, 03:00» в местном времени; строку, которая не дата, показываем как есть. */
export function formatMoment(iso: string): string {
  const moment = new Date(iso)
  if (Number.isNaN(moment.getTime())) return iso
  return moment.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

/** Текст для передачи администратору: заголовок, дословный текст ответа, код и идентификатор запроса. */
function reportOf(title: string, message: string, code: string, requestId: string | null): string {
  return [title, message, `Код: ${code}`, requestId === null ? null : `Запрос: ${requestId}`].filter((line) => line !== null).join('\n')
}

/** Отказ запроса или команды: текст службы дословно, код и идентификатор запроса — рядом. */
export function shownError(title: string, error: unknown): ShownError {
  const view = errorView(error)
  return { title, message: view.message, code: view.code, requestId: view.correlationId, report: reportOf(title, view.message, view.code, view.correlationId) }
}

/** Ошибка, которую служба записала в проверку или строку задачи (дословный ответ источника). */
export function serverErrorShown(title: string, message: string, code: string | null, requestId: string | null): ShownError {
  const shownCode = code ?? 'без кода'
  return { title, message, code: shownCode, requestId, report: reportOf(title, message, shownCode, requestId) }
}
